import { useState } from "react";
import { Button, TextField, Typography } from "@mui/material";
import { toast } from "react-toastify";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import { useCreateSerialEntityMutation } from "app/api/checklists";
import { useOrderContext } from "context/OrderContext";
import LargeDialog from "components/LargeDialog";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import AlertAccordion from "components/AlertAccordion";

type Props = {
  checklistId: string;
  onClose?: () => void;
};

const AddSerialEntityDialog: React.FC<Props> = ({ checklistId, onClose }) => {
  const order = useOrderContext();
  const [addProduct, { isLoading }] = useCreateSerialEntityMutation();

  const [showAllFields, setShowAllFields] = useState<boolean>(false);

  // Variables for new serial form
  const [serialNumber, setSerialNumber] = useState<string>("");
  const [maker, setMaker] = useState<string>("");
  const [model, setModel] = useState<string>("");
  const [macAddressWired, setMacAddressWired] = useState<string>("");
  const [ethernetPort, setEthernetPort] = useState<string>("");
  const [theftMarking, setTheftMarking] = useState<string>("");
  const [macAddressWifi, setMacAddressWifi] = useState<string>("");
  const [ipAddress, setIpAddress] = useState<string>("");
  const [username, setUsername] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [other, setOther] = useState<string>("");

  const handleEditSerial = async () => {
    addProduct({
      orderId: order.ordernr,
      checklistId,
      serialNumber: serialNumber,
      maker: maker,
      model: model,
      macAddress: macAddressWired,
      macAddressWifi: macAddressWifi,
      networkSocket: ethernetPort,
      theftProtection: theftMarking,
      ipAddress: ipAddress,
      username: username,
      password: password,
      other: other,
    })
      .unwrap()
      .then(() => {
        toast.success("Produkten har skapats!", {
          autoClose: 3000,
          hideProgressBar: true,
        });

        onClose?.();
      })
      .catch(() => {
        toast.error("Något gick fel när produkten skulle skapas.", {
          autoClose: 3000,
          hideProgressBar: true,
        });
      });
  };

  return (
    <LargeDialog
      onClose={() => onClose?.()}
      title="Skapa ny produkt"
      onSubmit={(e) => {
        e.preventDefault();
        handleEditSerial();
      }}
      actions={
        <>
          <Button
            variant="outlined"
            disabled={isLoading}
            startIcon={<CloseIcon />}
            onClick={onClose}
          >
            Avbryt
          </Button>

          <Button
            type="submit"
            variant="contained"
            color="success"
            startIcon={<SaveIcon />}
            loading={isLoading}
          >
            Spara
          </Button>
        </>
      }
    >
      <AlertAccordion severity="warning" title="Befintlig produkt">
        <Typography gutterBottom>
          Produkter skapade på detta sätt kopplas inte till någon artikel i Pyramid.
        </Typography>
        <Typography>
          Använd endast för att registrera produkter som inte levereras som en del av denna order,
          men som är installerade i rummet sedan tidigare.
        </Typography>
      </AlertAccordion>

      <TextField
        variant="filled"
        label="Serienummer"
        fullWidth
        required
        value={serialNumber}
        onChange={(e) => setSerialNumber(e.target.value)}
      />
      <TextField
        variant="filled"
        label="Tillverkare"
        fullWidth
        required
        value={maker}
        onChange={(e) => setMaker(e.target.value)}
      />
      <TextField
        variant="filled"
        label="Modell"
        fullWidth
        required
        value={model}
        onChange={(e) => setModel(e.target.value)}
      />
      <TextField
        variant="filled"
        label="MAC-adress"
        fullWidth
        hidden={!showAllFields && !macAddressWired}
        value={macAddressWired}
        onChange={(e) => setMacAddressWired(e.target.value)}
      />
      <TextField
        variant="filled"
        label="MAC-adress Wi-Fi"
        fullWidth
        hidden={!showAllFields && !macAddressWifi}
        value={macAddressWifi}
        onChange={(e) => setMacAddressWifi(e.target.value)}
      />
      <TextField
        variant="filled"
        label="Nätverksuttag"
        fullWidth
        hidden={!showAllFields && !ethernetPort}
        value={ethernetPort}
        onChange={(e) => setEthernetPort(e.target.value)}
      />
      <TextField
        variant="filled"
        label="Stöldmärkning"
        fullWidth
        hidden={!showAllFields && !theftMarking}
        value={theftMarking}
        onChange={(e) => setTheftMarking(e.target.value)}
      />
      <TextField
        variant="filled"
        label="IP-adress (om fast)"
        fullWidth
        hidden={!showAllFields && !ipAddress}
        value={ipAddress}
        onChange={(e) => setIpAddress(e.target.value)}
      />
      <TextField
        variant="filled"
        label="Användarnamn"
        fullWidth
        hidden={!showAllFields && !username}
        value={username}
        onChange={(e) => setUsername(e.target.value)}
      />
      <TextField
        variant="filled"
        label="Lösenord"
        fullWidth
        hidden={!showAllFields && !password}
        value={password}
        onChange={(e) => setPassword(e.target.value)}
      />
      <TextField
        variant="filled"
        label="Övrigt"
        fullWidth
        hidden={!showAllFields && !other}
        value={other}
        onChange={(e) => setOther(e.target.value)}
      />

      <Button
        startIcon={showAllFields ? <ExpandLess /> : <ExpandMore />}
        onClick={() => setShowAllFields(!showAllFields)}
        sx={{ mt: -1, mb: -2 }}
      >
        {showAllFields ? "Dölj tomma fält" : "Visa fler fält"}
      </Button>
    </LargeDialog>
  );
};

export default AddSerialEntityDialog;
